import NormalFeed from '@/components/NormalFeed'
import RelayInfo from '@/components/RelayInfo'
import SearchInput from '@/components/SearchInput'
import { useFetchRelayInfo } from '@/hooks/useFetchRelayInfo'
import { normalizeUrl } from '@/lib/url'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NotFound from './NotFound'
import { visitedRelaysAtom } from '@/atoms/memberships'
import { useSetAtom } from 'jotai'
import { appendUnique } from '@nostr/gadgets/utils'

export default function Relay({ url, className }: { url?: string; className?: string }) {
  const { t } = useTranslation()
  const normalizedUrl = useMemo(() => (url ? normalizeUrl(url) : undefined), [url])
  const { relayInfo } = useFetchRelayInfo(normalizedUrl ?? '')
  const [searchInput, setSearchInput] = useState('')
  const [debouncedInput, setDebouncedInput] = useState(searchInput)

  const subRequests = useMemo(
    () =>
      normalizedUrl
        ? [
            {
              source: 'relays' as const,
              urls: [normalizedUrl],
              filter: debouncedInput ? { search: debouncedInput } : {}
            }
          ]
        : [],
    [normalizedUrl, debouncedInput]
  )

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInput(searchInput)
    }, 1000)

    return () => {
      clearTimeout(handler)
    }
  }, [searchInput])

  const setVisitedRelays = useSetAtom(visitedRelaysAtom)

  useEffect(() => {
    setVisitedRelays((curr) => {
      appendUnique(curr, url)
      return [...curr]
    })
  }, [url])

  if (!normalizedUrl) return <NotFound />

  return (
    <div className={className}>
      <RelayInfo url={normalizedUrl} className="pt-3" />
      {relayInfo?.supported_nips?.includes?.(50) && (
        <div className="px-4 py-2">
          <SearchInput
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder={t('Search')}
          />
        </div>
      )}
      <NormalFeed subRequests={subRequests} showRelayCloseReason />
    </div>
  )
}
